import { FC, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { EnpsPerformanceCardSkeleton } from './EnpsPerformanceCardSkeleton';
import { cva } from 'class-variance-authority';

import { cn } from '@/utils/tailwind';

import { TrendDownIcon, TrendUpIcon, UsersIcon } from '../../../assets/icons';
import { ENPS_PERFORMANCE_CARD_TEST_IDS } from '../../../constants/data-testids';
import { Card, CardContent } from '../../atoms/Card/Card';
import { Tooltip } from '../../atoms/Tooltip/Tooltip';

type CardVariant = 'success' | 'declined' | 'neutral';

type EnpsPerformanceCardProps = {
  title: string;
  score: number | null | undefined;
  comparedToAverage: number | null | undefined;
  responseCount?: number;
  isLoading?: boolean;
  onCardClick?: () => void;
};

const colorVariant = cva('', {
  variants: {
    variant: {
      success: '!text-custom-emerald-700',
      declined: '!text-custom-red-700',
      neutral: '!text-custom-warm-grey-700',
    },
  },
  defaultVariants: {
    variant: 'neutral',
  },
});

const backgroundSliderVariant = cva('', {
  variants: {
    variant: {
      success: 'bg-custom-green-500',
      declined: 'bg-custom-red-400',
      neutral: 'bg-custom-orange-500',
    },
  },
  defaultVariants: {
    variant: 'neutral',
  },
});

const baselineMarkerVariant = cva('border-2 border-white bg-custom-warm-grey-100', {
  variants: {
    variant: {
      success: 'bg-custom-green-300',
      declined: 'bg-custom-warm-grey-200',
      neutral: 'bg-custom-orange-300',
    },
  },
  defaultVariants: {
    variant: 'neutral',
  },
});

const thumbVariant = cva('border-2 bg-white', {
  variants: {
    variant: {
      success: 'border-custom-green-500',
      declined: 'border-custom-red-400',
      neutral: 'border-custom-orange-500',
    },
  },
  defaultVariants: {
    variant: 'neutral',
  },
});

const clampPercentage = (value: number) => Math.min(Math.max(value, 0), 100);
const getEnpsPosition = (value: number) => clampPercentage((value + 100) / 2);
const SCORE_SEGMENT_WIDTHS = [2, 1, 1] as const;
const SCORE_SEGMENT_TOTAL_WIDTH = SCORE_SEGMENT_WIDTHS.reduce((sum, width) => sum + width, 0);
const FIRST_SCORE_SEGMENT_END = (SCORE_SEGMENT_WIDTHS[0] / SCORE_SEGMENT_TOTAL_WIDTH) * 100;
const SECOND_SCORE_SEGMENT_END =
  ((SCORE_SEGMENT_WIDTHS[0] + SCORE_SEGMENT_WIDTHS[1]) / SCORE_SEGMENT_TOTAL_WIDTH) * 100;

const getActiveScoreSegmentIndex = (scorePercentage: number) => {
  if (scorePercentage <= FIRST_SCORE_SEGMENT_END) {
    return 0;
  }

  if (scorePercentage <= SECOND_SCORE_SEGMENT_END) {
    return 1;
  }

  return 2;
};

const getSliderVariant = (score: number, comparedToAverage: number): CardVariant => {
  if (comparedToAverage > 0) {
    return 'success';
  }

  if (comparedToAverage < 0 && score < 0) {
    return 'declined';
  }

  return 'neutral';
};

export const EnpsPerformanceCard: FC<EnpsPerformanceCardProps> = ({
  title,
  score,
  comparedToAverage,
  responseCount,
  isLoading,
  onCardClick,
}) => {
  const { t } = useTranslation('components');

  const interactiveProps = onCardClick
    ? {
        role: 'button' as const,
        tabIndex: 0,
        onClick: onCardClick,
        onKeyDown: (e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onCardClick();
          }
        },
      }
    : {};

  const interactiveClassName = onCardClick
    ? 'cursor-pointer hover:bg-custom-warm-grey-25/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-custom-warm-grey-300'
    : '';

  if (isLoading) {
    return <EnpsPerformanceCardSkeleton />;
  }

  if (
    score === null ||
    score === undefined ||
    comparedToAverage === null ||
    comparedToAverage === undefined
  ) {
    return (
      <Card
        aria-label={`${title} eNPS summary`}
        className={cn(
          'h-[158px] rounded-xl border-custom-warm-grey-100 bg-white p-4',
          interactiveClassName
        )}
        {...interactiveProps}
      >
        <CardContent className="flex flex-col">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-body-14-semibold !text-custom-warm-grey-700 mb-2">{title}</p>
              <p className="text-body-14-regular !text-custom-warm-grey-500">
                {t('teamPerformanceCard.notEnoughResponses')}
              </p>
            </div>
            {responseCount !== undefined && (
              <span className="flex items-center gap-1 text-body-12-semibold text-custom-warm-grey-500">
                <UsersIcon width={16} height={16} />
                {responseCount}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  const comparisonVariant: CardVariant =
    comparedToAverage > 0 ? 'success' : comparedToAverage < 0 ? 'declined' : 'neutral';
  const scoreVariant: CardVariant = score > 0 ? 'success' : score < 0 ? 'declined' : 'neutral';
  const sliderVariant = getSliderVariant(score, comparedToAverage);
  const baseline = score - comparedToAverage;
  const scorePercentage = getEnpsPosition(score);
  const baselinePercentage = getEnpsPosition(baseline);
  const activeScoreSegmentIndex = getActiveScoreSegmentIndex(scorePercentage);
  const companyBaselineText = t('teamPerformanceCard.companyBaseline', { score: baseline });

  const getBaseLineText = () => {
    switch (comparisonVariant) {
      case 'success':
        return t('teamPerformanceCard.comparedToAverage.more');
      case 'declined':
        return t('teamPerformanceCard.comparedToAverage.less');
      case 'neutral':
        return t('teamPerformanceCard.comparedToAverage.same');
    }
  };

  const renderTrendIcon = () => {
    switch (comparisonVariant) {
      case 'success':
        return <TrendUpIcon width={19} height={19} />;
      case 'declined':
        return <TrendDownIcon width={19} height={19} />;
      case 'neutral':
        return (
          <span className="text-body-20-semibold text-custom-warm-grey-500" aria-hidden>
            —
          </span>
        );
    }
  };

  return (
    <Card
      aria-label={`${title} eNPS summary`}
      className={cn('rounded-xl border-custom-warm-grey-100 bg-white p-4', interactiveClassName)}
      {...interactiveProps}
    >
      <CardContent className="flex flex-col gap-6">
        <header className="flex flex-col gap-2">
          <div className="flex min-h-5 items-center justify-between gap-2">
            <p className="text-body-14-semibold !text-custom-warm-grey-700">{title}</p>
            <div className="flex items-center gap-2 text-custom-warm-grey-500">
              {responseCount !== undefined && (
                <span className="flex items-center gap-1 text-body-12-semibold">
                  <UsersIcon width={16} height={16} />
                  {responseCount}
                </span>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-0.5">
            <div
              className={cn(
                'flex min-h-6 items-center gap-1.5',
                colorVariant({ variant: scoreVariant })
              )}
            >
              <span className="text-body-20-semibold">{score}</span>
              {renderTrendIcon()}
            </div>
            <p className="flex min-h-6 items-center gap-1.5">
              {comparisonVariant === 'neutral' ? (
                <span className="text-body-14-regular !text-custom-warm-grey-500">
                  {getBaseLineText()}
                </span>
              ) : (
                <>
                  <span
                    className={cn(
                      'text-body-14-semibold',
                      colorVariant({ variant: comparisonVariant })
                    )}
                  >
                    {Math.abs(comparedToAverage)}
                  </span>
                  <span className="text-body-14-regular !text-custom-warm-grey-700">
                    {getBaseLineText()}
                  </span>
                </>
              )}
            </p>
          </div>
        </header>

        <div
          className="relative mb-[-11px] h-7 w-full"
          role="meter"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={scorePercentage}
        >
          <div className="absolute left-0 top-1/2 grid h-3 w-full -translate-y-1/2 grid-cols-[2fr_1fr_1fr] gap-1">
            {Array.from({ length: 3 }).map((_, index) => (
              <span
                key={index}
                className={cn(
                  'h-full rounded-full bg-custom-warm-grey-50',
                  index === activeScoreSegmentIndex &&
                    backgroundSliderVariant({ variant: sliderVariant })
                )}
              />
            ))}
          </div>
          <Tooltip
            title={companyBaselineText}
            triggerAriaLabel={companyBaselineText}
            triggerTestId={ENPS_PERFORMANCE_CARD_TEST_IDS.BASELINE_MARKER}
            triggerClassName={cn(
              'absolute top-1/2 h-6 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full',
              baselineMarkerVariant({ variant: sliderVariant })
            )}
            triggerStyle={{ left: `${baselinePercentage}%` }}
          />
          <span
            data-testid={ENPS_PERFORMANCE_CARD_TEST_IDS.SCORE_MARKER}
            className={cn(
              'absolute top-1/2 h-6 w-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full shadow-sm',
              thumbVariant({ variant: sliderVariant })
            )}
            style={{ left: `${scorePercentage}%` }}
          />
        </div>
      </CardContent>
    </Card>
  );
};
